<template>
  <ModalStep :id="'edit-profile-information-content'" :header="{ title: t('auth.editProfile.description.title') }">
    <template #bodyContent>
      <form>
        <TiptapEditor
          v-model="descriptionRef"
          :placeholder="t('auth.editProfile.description.placeholder')"
          :aria-label="t('auth.editProfile.description.title')"
          data-test-id="description-editor"
        />
      </form>
    </template>
    <template #footer>
      <div class="c-btns-container">
        <CustomButton
          usage="button"
          type="button"
          color="white"
          @emit:click="closeModal"
          :text="t('ui.button.cancel')"
        />
        <CustomButton
          usage="button"
          type="button"
          color="primary"
          data-test-id="profile-description-submit"
          :is-loading="isSubmittingRef"
          @emit:click="submitForm"
          :text="t('ui.button.confirm')"
        />
      </div>
    </template>
  </ModalStep>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type { ZodError } from 'zod'
import z from 'zod'

import { V1UpdateUserDescriptionMutation } from '@/features/auth/api'
import useAccount from '@/features/auth/hooks/use-account.hook'
import { selectGetSelectedProgramId } from '@/features/programs/stores/programs/programs.selectors'
import programsStore from '@/features/programs/stores/programs/programs.store'
import CustomButton from '@/features/ui/components/button/custom-button.vue'
import ModalStep from '@/features/ui/components/modal/modal-step/modal-step.vue'
import TiptapEditor from '@/features/ui/components/tiptap-editor/tiptap-editor.vue'
import modalsStore from '@/features/ui/store/modal/modal.store'
import useUser from '@/features/users/hooks/use-user.hook'
import useToast from '@/hooks/use-toasts.hook'
import { useI18n } from '@/lib/i18n'
import { invalidateAccount, invalidateGroups, invalidateUser } from '@/utils/lib/vue-query'

const { t } = useI18n()
const toast = useToast()
const { accountRef } = useAccount()
const selectedProgramIdRef = selectGetSelectedProgramId(programsStore)
const accountIdRef = computed(() => accountRef.value?.id)
const { userRef } = useUser(accountIdRef)

const descriptionRef = ref<string>('')
const isSubmittingRef = ref(false)

watch(
  userRef,
  (user) => {
    if (user) {
      descriptionRef.value = user.description || ''
    }
  },
  { immediate: true },
)

const validationSchema = z.object({
  description: z.string(),
})

async function submitForm() {
  try {
    isSubmittingRef.value = true
    await validationSchema.parseAsync({ description: descriptionRef.value })
    await V1UpdateUserDescriptionMutation({
      userId: accountRef.value!.id!,
      description: descriptionRef.value,
    })
    await invalidateUser(accountRef.value!.id!)
    await invalidateAccount()
    await invalidateGroups(selectedProgramIdRef.value!)
    toast?.success(t('auth.editProfile.success'))
    closeModal()
  } catch (e: any) {
    const error: ZodError = e
    if (error.errors) {
      error.errors.forEach((err) => toast?.error(err.message))
    } else {
      toast?.error(t('error.common'))
    }
  } finally {
    isSubmittingRef.value = false
  }
}

function closeModal() {
  modalsStore.toggleModal('profile-description', false)
}
</script>

<style lang="scss">
// Styles removed - using Tiptap component styles
</style>
